import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Archive,
  Check,
  Clock,
  Copy,
  Layers,
  Mail,
  Pencil,
  Plus,
  Settings as SettingsIcon,
  Share2,
  Trash2,
} from "lucide-react";
import {
  accountApi,
  factorsApi,
  publicShareApi,
  strategiesApi,
  type FactorRecord,
  type Strategy,
} from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSaveSettings, useSettings } from "../lib/settings";
import TimezonePicker from "../components/TimezonePicker";
import Select from "../components/Select";

export default function SettingsPage() {
  const { t } = useI18n();
  return (
    <>
      <header>
        <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("nav.settings")}</p>
        <h1 className="text-3xl font-bold text-parchment mt-1 flex items-center gap-2">
          <SettingsIcon className="w-6 h-6 text-accent" />
          {t("settings.account")}
        </h1>
      </header>

      <TimezonesCard />
      <PublicProfileCard />
      <WeeklyDigestCard />
      <StrategiesCard />
      <FactorsCard />
      <DangerZoneCard />

      {/* Which build is actually running. Worth showing: the service worker
          caches the app shell, so "is my fix live?" is a real question, and
          this is the only place to answer it without opening devtools. */}
      <p className="text-center text-xs text-parchment-muted pb-2">
        {t("settings.version")} <span className="tabular">{__APP_VERSION__}</span>
      </p>
    </>
  );
}

// ------------- Small toggle switch -------------
function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 " +
        (checked ? "bg-accent" : "bg-parchment-muted/30")
      }
    >
      <span
        className={
          "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform " +
          (checked ? "translate-x-5 rtl:-translate-x-5" : "translate-x-0.5 rtl:-translate-x-0.5")
        }
      />
    </button>
  );
}

// ------------- Public profile (shareable stats) -------------
function PublicProfileCard() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { settings } = useSettings();
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const isPublic = Boolean(settings.share_public);
  const shareUrl = settings.share_url ?? null;

  const toggleM = useMutation({
    mutationFn: (next: boolean) => (next ? publicShareApi.enable() : publicShareApi.disable()),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["user-settings"] });
    },
    onError: (e) => setError((e as Error).message),
  });

  async function copy() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be blocked — ignore */
    }
  }

  return (
    <section className="glass p-6">
      <h2 className="text-lg font-semibold text-parchment mb-2 flex items-center gap-2">
        <Share2 className="w-5 h-5 text-accent" />
        {t("settings.publicProfile")}
      </h2>
      <p className="text-parchment-muted text-sm mb-4">{t("settings.publicProfileHelp")}</p>

      <div className="flex items-center gap-3">
        <Toggle
          checked={isPublic}
          disabled={toggleM.isPending}
          onChange={(next) => toggleM.mutate(next)}
          label={t("settings.publicProfileToggle")}
        />
        <span className="text-sm text-parchment">
          {isPublic ? t("settings.publicProfileOn") : t("settings.publicProfileOff")}
        </span>
      </div>

      {isPublic && shareUrl && (
        <div className="mt-4 flex flex-wrap items-center gap-2 max-w-xl">
          <input readOnly value={shareUrl} className="input flex-1 min-w-[220px]" onFocus={(e) => e.target.select()} />
          <button type="button" onClick={copy} className="btn-gold">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? t("settings.copied") : t("settings.copyLink")}
          </button>
        </div>
      )}

      {error && (
        <p className="mt-3 rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-pnl-neg">
          {error}
        </p>
      )}
    </section>
  );
}

// ------------- Weekly email digest -------------
function WeeklyDigestCard() {
  const { t } = useI18n();
  const { settings } = useSettings();
  const saveM = useSaveSettings();
  const [error, setError] = useState<string | null>(null);

  const on = Boolean(settings.weekly_digest);

  return (
    <section className="glass p-6">
      <h2 className="text-lg font-semibold text-parchment mb-2 flex items-center gap-2">
        <Mail className="w-5 h-5 text-accent" />
        {t("settings.weeklyDigest")}
      </h2>
      <p className="text-parchment-muted text-sm mb-4">{t("settings.weeklyDigestHelp")}</p>

      <div className="flex items-center gap-3">
        <Toggle
          checked={on}
          disabled={saveM.isPending}
          onChange={(next) => {
            setError(null);
            saveM.mutate(
              { weekly_digest: next },
              { onError: (e) => setError((e as Error).message) },
            );
          }}
          label={t("settings.weeklyDigestToggle")}
        />
        <span className="text-sm text-parchment">
          {on ? t("settings.weeklyDigestOn") : t("settings.weeklyDigestOff")}
        </span>
      </div>

      {error && (
        <p className="mt-3 rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-pnl-neg">
          {error}
        </p>
      )}
    </section>
  );
}

// ------------- Timezones -------------
function TimezonesCard() {
  const { t } = useI18n();
  const { settings } = useSettings();
  const saveM = useSaveSettings();
  const [msg, setMsg] = useState<string | null>(null);

  function save(next: Partial<typeof settings>) {
    setMsg(null);
    saveM.mutate(next, {
      onSuccess: () => {
        setMsg(t("settings.savedMsg"));
        setTimeout(() => setMsg(null), 2000);
      },
      onError: (e) => setMsg((e as Error).message),
    });
  }

  return (
    <section className="glass p-6">
      <h2 className="text-lg font-semibold text-parchment mb-2 flex items-center gap-2">
        <Clock className="w-5 h-5 text-accent" />
        {t("settings.timezones")}
      </h2>
      <p className="text-parchment-muted text-sm mb-4">
        {t("settings.tzHelp")}
      </p>

      <div className="grid gap-4 sm:grid-cols-2 max-w-3xl">
        <div>
          <label className="label block mb-2" htmlFor="displayTz">
            {t("settings.displayTz")}
          </label>
          <TimezonePicker
            id="displayTz"
            value={settings.display_timezone}
            onChange={(tz) => save({ display_timezone: tz })}
          />
          <p className="mt-2 text-xs text-parchment-muted">
            {t("settings.displayTzHint")}
          </p>
        </div>

        <div>
          <label className="label block mb-2" htmlFor="mt5Tz">
            {t("settings.mt5Tz")}
          </label>
          <TimezonePicker
            id="mt5Tz"
            value={settings.mt5_import_timezone}
            onChange={(tz) => save({ mt5_import_timezone: tz })}
          />
          <p className="mt-2 text-xs text-parchment-muted">
            {t("settings.mt5TzHint")}
          </p>
        </div>
      </div>

      {msg && (
        <p
          className={
            "mt-3 rounded-lg px-3 py-2 text-sm " +
            (msg === t("settings.savedMsg")
              ? "bg-emerald-500/10 border border-emerald-500/30 text-pnl-pos"
              : "bg-rose-500/10 border border-rose-500/30 text-pnl-neg")
          }
        >
          {msg}
        </p>
      )}
    </section>
  );
}

// ------------- Strategies -------------
function StrategiesCard() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["strategies"], queryFn: () => strategiesApi.list() });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const createM = useMutation({
    mutationFn: () =>
      strategiesApi.create({ name: name.trim(), description: description.trim() || undefined }),
    onSuccess: () => {
      setName("");
      setDescription("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["strategies"] });
    },
    onError: (e) => setError((e as Error).message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim()) createM.mutate();
  }

  const rows = q.data?.strategies ?? [];

  return (
    <section className="glass p-6">
      <h2 className="text-lg font-semibold text-parchment mb-2 flex items-center gap-2">
        <Layers className="w-5 h-5 text-accent" />
        {t("settings.strategies")}
      </h2>
      <p className="text-parchment-muted text-sm mb-4">
        {t("settings.strategiesHelp")}
      </p>

      <form onSubmit={submit} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto] mb-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("settings.strategyNamePlaceholder")}
          maxLength={120}
          className="input"
        />
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("settings.strategyDescPlaceholder")}
          className="input"
        />
        <button type="submit" disabled={createM.isPending || !name.trim()} className="btn-gold">
          <Plus className="w-4 h-4" />
          {t("settings.add")}
        </button>
      </form>

      {error && (
        <p className="mb-3 rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-pnl-neg">
          {error}
        </p>
      )}

      <ul className="divide-y divide-glass">
        {rows.length === 0 && !q.isLoading && (
          <li className="py-6 text-center text-parchment-muted text-sm">{t("settings.noStrategies")}</li>
        )}
        {rows.map((s) => (
          <StrategyRow key={s.id} strategy={s} />
        ))}
      </ul>
    </section>
  );
}

function StrategyRow({ strategy: s }: { strategy: Strategy }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(s.name);
  const [description, setDescription] = useState(s.description ?? "");

  const saveM = useMutation({
    mutationFn: () => strategiesApi.update(s.id, { name: name.trim(), description }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["strategies"] });
    },
  });
  const destroyM = useMutation({
    mutationFn: () => strategiesApi.destroy(s.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["strategies"] }),
  });
  const toggleM = useMutation({
    mutationFn: () => strategiesApi.update(s.id, { status: s.status === "active" ? "archived" : "active" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["strategies"] }),
  });

  return (
    <li className="py-3 flex items-center gap-3">
      {editing ? (
        <>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input flex-1 min-w-0"
          />
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input flex-1 min-w-0"
            placeholder={t("settings.descriptionPlaceholder")}
          />
          <button
            type="button"
            onClick={() => saveM.mutate()}
            disabled={saveM.isPending}
            className="btn-gold"
          >
            {t("common.save")}
          </button>
          <button type="button" onClick={() => setEditing(false)} className="btn-ghost">
            {t("common.cancel")}
          </button>
        </>
      ) : (
        <>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-parchment truncate flex items-center gap-2">
              {s.name}
              {s.status === "archived" && (
                <span className="chip-gold text-[10px] px-2 py-0.5">{t("settings.archived")}</span>
              )}
            </p>
            {s.description && (
              <p className="text-xs text-parchment-muted truncate">{s.description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-parchment-muted hover:text-parchment"
            title={t("common.edit")}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => toggleM.mutate()}
            disabled={toggleM.isPending}
            className="text-parchment-muted hover:text-parchment"
            title={s.status === "active" ? t("settings.archive") : t("settings.unarchive")}
          >
            <Archive className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (confirm(t("settings.deleteStrategyConfirm", { name: s.name }))) destroyM.mutate();
            }}
            disabled={destroyM.isPending}
            className="text-parchment-muted hover:text-pnl-neg"
            title={t("common.delete")}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </>
      )}
    </li>
  );
}

// ------------- Factors -------------
function FactorsCard() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["factors"], queryFn: () => factorsApi.list() });
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<"positive" | "negative">("positive");

  const createM = useMutation({
    mutationFn: () => factorsApi.create({ label: label.trim(), kind }),
    onSuccess: () => {
      setLabel("");
      qc.invalidateQueries({ queryKey: ["factors"] });
    },
  });

  const rows = q.data?.factors ?? [];

  return (
    <section className="glass p-6">
      <h2 className="text-lg font-semibold text-parchment mb-2">{t("settings.factors")}</h2>
      <p className="text-parchment-muted text-sm mb-4">
        {t("settings.factorsHelp")}
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (label.trim()) createM.mutate();
        }}
        className="flex flex-wrap gap-2 mb-4"
      >
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t("settings.factorPlaceholder")}
          maxLength={120}
          className="input flex-1 min-w-[180px]"
        />
        <Select
          value={kind}
          onChange={(v) => setKind(v as "positive" | "negative")}
          className="w-auto"
          options={[
            { value: "positive", label: t("settings.factorPositive") },
            { value: "negative", label: t("settings.factorNegative") },
          ]}
        />
        <button type="submit" disabled={createM.isPending || !label.trim()} className="btn-gold">
          <Plus className="w-4 h-4" />
          {t("settings.add")}
        </button>
      </form>

      <ul className="divide-y divide-glass">
        {rows.length === 0 && !q.isLoading && (
          <li className="py-6 text-center text-parchment-muted text-sm">{t("settings.noFactors")}</li>
        )}
        {rows.map((f) => (
          <FactorRow key={f.id} factor={f} />
        ))}
      </ul>
    </section>
  );
}

function FactorRow({ factor: f }: { factor: FactorRecord }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const destroyM = useMutation({
    mutationFn: () => factorsApi.destroy(f.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["factors"] }),
  });
  return (
    <li className="py-3 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-parchment truncate flex items-center gap-2">
          {f.label}
          <span
            className={
              "inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border " +
              (f.kind === "positive"
                ? "bg-emerald-500/15 text-pnl-pos border-emerald-500/30"
                : "bg-rose-500/15 text-pnl-neg border-rose-500/30")
            }
          >
            {f.kind}
          </span>
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          if (confirm(t("settings.deleteFactorConfirm", { label: f.label }))) destroyM.mutate();
        }}
        disabled={destroyM.isPending}
        className="text-parchment-muted hover:text-pnl-neg"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}

// ------------- Danger zone -------------
function DangerZoneCard() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [confirmText, setConfirmText] = useState("");
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const resetM = useMutation({
    mutationFn: () => accountApi.reset(),
    onSuccess: (r) => {
      setConfirmText("");
      setMsg({
        text: t("settings.resetSuccess", {
          trades: r.trades_deleted,
          wallet: r.wallet_deleted,
        }),
        ok: true,
      });
      // Refresh everything that reads trades / wallet.
      for (const key of [
        "trades",
        "wallet",
        "wallet-analytics",
        "dashboard-summary",
        "reports-overview",
        "journey",
        "mt5-imports",
      ]) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (e) => setMsg({ text: (e as Error).message || t("settings.resetFailed"), ok: false }),
  });

  const armed = confirmText.trim().toUpperCase() === "RESET";

  function onReset() {
    if (!armed || resetM.isPending) return;
    if (!confirm(t("settings.resetConfirm"))) return;
    setMsg(null);
    resetM.mutate();
  }

  return (
    <section className="glass p-6 border border-rose-500/30">
      <h2 className="text-lg font-semibold text-pnl-neg mb-2 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" />
        {t("settings.dangerZone")}
      </h2>
      <p className="text-parchment-muted text-sm mb-4">
        {t("settings.dangerLead")}{" "}
        <span className="text-parchment font-semibold">{t("settings.dangerAll")}</span>{" "}
        {t("settings.dangerMid")}{" "}
        <span className="font-semibold">{t("settings.dangerCannotUndo")}</span>.
      </p>

      <div className="flex flex-wrap items-center gap-2 max-w-lg">
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={t("settings.resetPlaceholder")}
          className="input flex-1 min-w-[180px]"
          aria-label={t("settings.resetPlaceholder")}
        />
        <button
          type="button"
          onClick={onReset}
          disabled={!armed || resetM.isPending}
          className="btn-ghost border-rose-500/40 text-pnl-neg hover:bg-rose-500/10 disabled:opacity-40"
        >
          <Trash2 className="w-4 h-4" />
          {resetM.isPending ? t("settings.resetting") : t("settings.deleteAllReset")}
        </button>
      </div>

      {msg && (
        <p
          className={
            "mt-3 rounded-lg px-3 py-2 text-sm " +
            (msg.ok
              ? "bg-emerald-500/10 border border-emerald-500/30 text-pnl-pos"
              : "bg-rose-500/10 border border-rose-500/30 text-pnl-neg")
          }
        >
          {msg.text}
        </p>
      )}
    </section>
  );
}
